import {Button, message, Progress, Space} from "antd";
import {StyledCoverImagesItemListWrapper, StyledCoverImagesWrapper} from "./styles";
import {
  OfferingCoverImagesUploadFileItem,
  OfferingCoverImagesUploadProps,
  OfferingCoverImagesUploadStatus,
} from "./types";
import {OfferingFormCoverImagesItem} from "./cover-images-item";
import {useEffect, useMemo, useRef, useState} from "react";
import type {CoverImagesItemResponse} from "../../../../react-query-hooks/products/types";
import {PlusCircleFilled} from "@ant-design/icons";
import axios, {type AxiosResponse} from "axios";

function syntheticCoverImagesFromUrls(
  urls: string[],
  offeringId: number | string
): CoverImagesItemResponse[] {
  const oid = Number(offeringId);
  const stamp = Date.now();
  return urls.map((url, index) => ({
    id: `upload-${stamp}-${index}`,
    media_url: url,
    offering_id: Number.isFinite(oid) ? oid : 0,
    createdAt: "",
    updatedAt: "",
    media_type: "image" as const,
    offering_type: "product" as const,
    user_id: 0,
  }));
}

/** Accepts mutation return value (AxiosResponse) and common unwrapped / v2 body shapes. */
function mediaFilesFromCoverUploadResponse(
  response: unknown,
  offeringId: number | string
): CoverImagesItemResponse[] {
  if (!response || typeof response !== "object") {
    return [];
  }

  const asAxios = response as AxiosResponse<Record<string, unknown>>;
  const httpBody =
    typeof asAxios.data === "object" && asAxios.data !== null
      ? (asAxios.data as Record<string, unknown>)
      : (response as Record<string, unknown>);

  const inner =
    typeof httpBody.data === "object" && httpBody.data !== null
      ? (httpBody.data as Record<string, unknown>)
      : httpBody;

  const media_files = inner.media_files ?? httpBody.media_files;
  if (Array.isArray(media_files) && media_files.length > 0) {
    return media_files as CoverImagesItemResponse[];
  }

  const urlsFromInner = inner.mediaUrls;
  const urlsFromBody = httpBody.mediaUrls;
  const mediaUrls = Array.isArray(urlsFromInner)
    ? urlsFromInner
    : Array.isArray(urlsFromBody)
    ? urlsFromBody
    : [];

  const stringUrls = mediaUrls.filter(
    (u): u is string => typeof u === "string" && u.trim().length > 0
  );
  if (stringUrls.length > 0) {
    return syntheticCoverImagesFromUrls(stringUrls, offeringId);
  }

  return [];
}

export function NewOfferingCoverImagesUpload({
  defaultCoverImages,
  offeringId,
  isDeleteLoading,
  isUploadLoading,
  deleteCoverImage,
  uploadCoverImages,
  offeringType,
}: OfferingCoverImagesUploadProps) {
  const MAX_FILES = 5;

  const [uploads, setUploads] = useState<OfferingCoverImagesUploadFileItem[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleContentUploadButtonClick() {
    fileInputRef.current?.click();
  }

  // Sync server data with local state:
  // - Keep pending/uploading items (user-initiated, not yet on server)
  // - Replace completed items with server data (source of truth)
  // - Remove items deleted from server
  useEffect(() => {
    setUploads((prev) => {
      // Keep only pending/uploading items (not yet on server)
      const pendingItems = prev.filter(
        (u) => u.status === "pending" || u.status === "uploading"
      );

      // Convert server data to local format
      const serverItems: OfferingCoverImagesUploadFileItem[] = defaultCoverImages.map(
        (item) => ({
          id: item.id,
          file: {
            name: "",
            url: item.media_url,
          },
          progress: 100,
          status: "success" as const,
          controller: null,
        })
      );

      // Server items first, then pending uploads
      return [...serverItems, ...pendingItems];
    });
  }, [defaultCoverImages]);

  const createId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

  const startUpload = async (fileObj: OfferingCoverImagesUploadFileItem) => {
    const formData = new FormData();
    if (fileObj.file instanceof File) {
      formData.append("file", fileObj.file);
      formData.append("offeringId", `${offeringId}`);
      formData.append("offeringType", `${offeringType}`);
    }
    const controller = axios.CancelToken.source();

    setUploads((prev) =>
      prev.map((u) =>
        u.id === fileObj.id ? {...u, status: "uploading", progress: 0, controller} : u
      )
    );

    try {
      await uploadCoverImages.mutateAsync({
        data: formData,
        cancelToken: controller.token,
        onProgress: (progress: number) => {
          setUploads((prev) =>
            prev.map((u) => (u.id === fileObj.id ? {...u, progress} : u))
          );
        },
        offeringId,
      });

      // Upload complete - remove temp item from local state
      // The useEffect will add the image back when defaultCoverImages updates from cache
      setUploads((prev) => prev.filter((u) => u.id !== fileObj.id));
    } catch (err) {
      const isCancelled = axios.isCancel(err);
      setUploads((prev) =>
        prev.map((u) =>
          u.id === fileObj.id
            ? {
                ...u,
                status: isCancelled ? "cancelled" : "error",
                controller: null,
              }
            : u
        )
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const filesSelected = e.target.files;

    // Filter only image files
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      message.info("Please select image files only");
      return;
    }

    // Check if adding these files would exceed the limit
    if (defaultCoverImages.length + imageFiles.length > MAX_FILES) {
      message.info(
        `You can only select up to ${MAX_FILES} images. Currently selected: ${
          defaultCoverImages.length + imageFiles.length
        }`
      );
      return;
    }
    if (filesSelected && filesSelected.length > 0) {
      const newUploads: OfferingCoverImagesUploadFileItem[] = files.map((file) => ({
        id: createId(),
        file,
        previewUrl: URL.createObjectURL(file), // 👈
        progress: 0,
        status: "pending",
        controller: null,
      }));

      setUploads((prev) => [...prev, ...newUploads]);

      newUploads.forEach((fileObj) => startUpload(fileObj));
    } else {
      return message.error("No files ");
    }
  };

  const retryUpload = (fileObj: OfferingCoverImagesUploadFileItem) => {
    startUpload(fileObj);
  };

  // Optimistic delete: remove from local state immediately, then call parent
  const handleDelete = (mediaId: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== mediaId));
    deleteCoverImage({
      mediaId,
      offeringId: Number(offeringId),
    });
  };

  return (
    <StyledCoverImagesWrapper>
      {uploads.length > 0 && (
        <StyledCoverImagesItemListWrapper>
          <Space size={8} direction="vertical" style={{display: "flex"}}>
            {uploads.map((ci) => (
              <>
                <OfferingFormCoverImagesItem
                  key={ci.id}
                  deleteFunction={() => handleDelete(ci.id)}
                  imgSrc={
                    "previewUrl" in ci
                      ? String(ci.previewUrl)
                      : "url" in ci.file
                      ? ci.file.url
                      : ""
                  }
                  isDeleteLoading={isDeleteLoading}
                  uploadStatus={ci.status}
                />

                {(ci.status === "error" || ci.status === "cancelled") && (
                  <button
                    onClick={() => retryUpload(ci)}
                    className="ml-2 text-sm text-blue-600"
                  >
                    Retry
                  </button>
                )}
              </>
            ))}
          </Space>
        </StyledCoverImagesItemListWrapper>
      )}

      <div>
        <input
          style={{display: "none"}}
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleChange}
        />
        {defaultCoverImages.length < 5 && (
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <p style={{textAlign: "center"}}>
              Images should be horizontal, at least 1280x720px
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button
                onClick={handleContentUploadButtonClick}
                size="small"
                icon={<PlusCircleFilled />}
                disabled={isDeleteLoading}
                loading={isUploadLoading}
              >
                Upload images
              </Button>
            </div>
          </Space>
        )}
      </div>
    </StyledCoverImagesWrapper>
  );
}

type LocalBlobPreview = {tempId: string; previewUrl: string};

function createLocalPreviewId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function OfferingCoverImagesUpload({
  defaultCoverImages,
  offeringId,
  isDeleteLoading,
  isUploadLoading,
  deleteCoverImage,
  uploadCoverImages,
  offeringType,
  uploadButtonText = "Upload images",
  uploadButtonIcon,
}: OfferingCoverImagesUploadProps) {
  const MAX_FILES = 5;
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState<OfferingCoverImagesUploadStatus>("pending");
  /** Shown immediately after upload succeeds, until `defaultCoverImages` catches up from the server. */
  const [justUploadedCovers, setJustUploadedCovers] = useState<CoverImagesItemResponse[]>(
    []
  );
  /** Local object URLs so the user sees the image while upload runs and if the API body shape varies. */
  const [blobPreviews, setBlobPreviews] = useState<LocalBlobPreview[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const serverUrls = new Set(defaultCoverImages.map((c) => c.media_url));
    setJustUploadedCovers((prev) => prev.filter((c) => !serverUrls.has(c.media_url)));
  }, [defaultCoverImages]);

  const coverImagesToDisplay = useMemo(() => {
    const byUrl = new Map<string, CoverImagesItemResponse>();
    const oid = Number(offeringId) || 0;
    for (const c of defaultCoverImages) {
      if (c.media_url) {
        byUrl.set(c.media_url, c);
      }
    }
    for (const c of justUploadedCovers) {
      if (c.media_url && !byUrl.has(c.media_url)) {
        byUrl.set(c.media_url, c);
      }
    }
    for (const b of blobPreviews) {
      if (b.previewUrl && !byUrl.has(b.previewUrl)) {
        byUrl.set(b.previewUrl, {
          id: b.tempId,
          media_url: b.previewUrl,
          offering_id: oid,
          createdAt: "",
          updatedAt: "",
          media_type: "image",
          offering_type: "product",
          user_id: 0,
        });
      }
    }
    return Array.from(byUrl.values());
  }, [defaultCoverImages, justUploadedCovers, blobPreviews, offeringId]);

  function handleContentUploadButtonClick() {
    fileInputRef.current?.click();
  }
  return (
    <StyledCoverImagesWrapper>
      {coverImagesToDisplay.length > 0 && (
        <StyledCoverImagesItemListWrapper>
          <Space size={8} direction="vertical" style={{display: "flex"}}>
            {coverImagesToDisplay.map((ci) => (
              <OfferingFormCoverImagesItem
                key={ci.id}
                deleteFunction={() =>
                  deleteCoverImage({
                    mediaId: ci.id,
                    offeringId: ci.offering_id,
                  })
                }
                imgSrc={ci.media_url}
                isDeleteLoading={isDeleteLoading}
                uploadStatus={uploading}
              />
            ))}
          </Space>
        </StyledCoverImagesItemListWrapper>
      )}
      <div>
        {uploading !== "pending" && (
          <Progress
            percent={progress}
            status={
              uploading === "error"
                ? "exception"
                : uploading === "uploading"
                ? "active"
                : uploading === "success"
                ? "success"
                : uploading === "cancelled"
                ? "exception"
                : "normal"
            }
            strokeColor={uploading === "success" ? "#52c41a" : "#222222"}
          />
        )}
      </div>

      <div>
        <input
          style={{display: "none"}}
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={(e) => {
            if (e.target.files) {
              const files = Array.from(e.target.files);
              const filesSelected = e.target.files;

              // Filter only image files
              const imageFiles = files.filter((file) => file.type.startsWith("image/"));

              if (imageFiles.length === 0) {
                message.info("Please select image files only");
                return;
              }

              // Check if adding these files would exceed the limit
              if (coverImagesToDisplay.length + imageFiles.length > MAX_FILES) {
                message.info(
                  `You can only select up to ${MAX_FILES} images. Currently selected: ${
                    coverImagesToDisplay.length + imageFiles.length
                  }`
                );
                return;
              }
              if (filesSelected && filesSelected.length > 0) {
                if (offeringId) {
                  const batchBlobPreviews: LocalBlobPreview[] = imageFiles.map(
                    (file) => ({
                      tempId: createLocalPreviewId(),
                      previewUrl: URL.createObjectURL(file),
                    })
                  );
                  setBlobPreviews((prev) => [...prev, ...batchBlobPreviews]);

                  const revokeBatch = () => {
                    batchBlobPreviews.forEach((p) => URL.revokeObjectURL(p.previewUrl));
                    setBlobPreviews((prev) =>
                      prev.filter(
                        (p) => !batchBlobPreviews.some((b) => b.tempId === p.tempId)
                      )
                    );
                  };

                  const formData = new FormData();
                  Array.from(filesSelected).forEach((file) => {
                    formData.append("file", file);
                  });
                  formData.append("offeringId", `${offeringId}`);
                  formData.append("offeringType", `${offeringType}`);
                  setUploading("uploading");
                  setProgress(0);
                  uploadCoverImages.mutate(
                    {
                      data: formData,
                      offeringId,
                      onProgress: (progress: number) => {
                        setProgress(progress);
                      },
                    },
                    {
                      onSuccess: (data) => {
                        message.success(
                          `Uploaded ${filesSelected.length} file(s) successfully`
                        );
                        revokeBatch();
                        const uploaded = mediaFilesFromCoverUploadResponse(
                          data,
                          offeringId
                        );
                        if (uploaded.length > 0) {
                          setJustUploadedCovers((prev) => [...prev, ...uploaded]);
                        }
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                        setUploading("success");
                        setTimeout(() => {
                          setUploading("pending");
                        }, 3000);
                      },
                      onError: () => {
                        revokeBatch();
                        setUploading("error");
                      },
                    }
                  );
                }
              } else {
                return message.error("No files ");
              }
            }
          }}
        />
        {coverImagesToDisplay.length < 5 && (
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <p style={{textAlign: "center"}}>
              Images should be horizontal, at least 1280x720px
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button
                onClick={handleContentUploadButtonClick}
                size="small"
                icon={uploadButtonIcon ?? <PlusCircleFilled />}
                disabled={isDeleteLoading}
                loading={isUploadLoading}
              >
                {uploadButtonText}
              </Button>
            </div>
          </Space>
        )}
      </div>
    </StyledCoverImagesWrapper>
  );
}
